import { PDF_BUILTIN_KEYS } from '../../shared/columns';
import { builtinSourceLabel, describePdfSource, validatePdfPlacementBinding, type PdfBindingEvent } from '../../shared/pdfBindingValidation';
import type { BuiltinAnswers, BuiltinSourceKey, Field, PdfDocumentDefinition, PdfPlacement, PdfValueSource } from '../../shared/types';
export interface PdfSourceChoice { label: string; group: string; source: PdfValueSource;
  /** For a question that can fill a field more than one way: the question, and this way's short name beneath it. */
  question?: { name: string; entry: string } }
/** The section of the standard registration questions an answer is asked in, named as on the Questions page. */
function builtinSection(key: BuiltinSourceKey): string {
  const at = (column: keyof BuiltinAnswers) => PDF_BUILTIN_KEYS.indexOf(column), index = PDF_BUILTIN_KEYS.indexOf(key);
  return index < at('specialDiet') ? 'Contact information' : index < at('chronicIllness') ? 'Medical information' : 'Conditions that limit activity';
}
/** What a date field can show: the day the forms are signed, or that day as a parent or guardian's, filled only for a minor. */
export const SIGNING_DATES: { label: string; source: PdfValueSource }[] = [
  { label: 'Date signed', source: { kind: 'submittedDate' } },
  { label: 'Date parent or guardian signed', source: { kind: 'parentSignedDate' } },
];
export function pdfSourceChoices(event: PdfBindingEvent, document: PdfDocumentDefinition, placement: PdfPlacement): PdfSourceChoice[] {
  const choices: PdfSourceChoice[] = [];
  const add = (group: string, label: string, source: PdfValueSource, question?: PdfSourceChoice['question']) => {
    if (!validatePdfPlacementBinding(event, document, { ...placement, source }).length) choices.push({ group, label, source, ...(question ? { question } : {}) });
  };
  // The event's own details first: they're the same on every registration.
  for (const [key, label] of [['name','Event name'], ['dates','Activity dates'], ['stakeName','Stake name'], ['wardName','Ward name']] as const) add('Event details', label, { kind: 'event', key });
  for (const key of PDF_BUILTIN_KEYS) add(builtinSection(key), builtinSourceLabel(key), { kind: 'builtin', key });
  // Every way a question can fill a field: its answer, a number out of its maximum, each choice, each grid row and cell.
  const addQuestionChoices = (field: Field, group: string) => {
    const addQuestion = (projection: Extract<PdfValueSource, { kind: 'question' }>['projection']) => {
      const source: PdfValueSource = { kind: 'question', fieldId: field.id, projection };
      add(group, describePdfSource(document, source, event), source, { name: field.label.trim() || 'Untitled question', entry: questionEntry(field, projection) });
    };
    // A checkboxes question's choices go in checkbox fields, one each, rather than as a list of names in one text field.
    if (field.type !== 'checkboxes') addQuestion({ kind: 'text' });
    if (['linear_scale','rating'].includes(field.type)) addQuestion({ kind: 'text', numberFormat: 'fraction' });
    field.optionIds?.forEach(optionId => addQuestion({ kind: 'optionSelected', optionId }));
    if (field.allowOther && ['choice', 'checkboxes'].includes(field.type)) { addQuestion({ kind: 'otherText' }); addQuestion({ kind: 'otherSelected' }); }
    field.grid?.rowIds?.forEach(rowId => {
      addQuestion({ kind: 'gridRowText', rowId });
      field.grid!.columnIds?.forEach(columnId => addQuestion({ kind: 'gridCellSelected', rowId, columnId }));
    });
  };
  let section = '';
  for (const field of event.fields) {
    if (field.type === 'section') { section = field.label.trim() || 'Untitled section'; continue; }
    if (field.type === 'title_text') continue;
    addQuestionChoices(field, section || 'Registration questions');
  }
  for (const field of document.questions) addQuestionChoices(field, 'Questions for this form');
  add('Signatures and dates', 'Participant signature', { kind: 'signature', role: 'participant' });
  add('Signatures and dates', 'Parent or guardian signature', { kind: 'signature', role: 'parent' });
  add('Signatures and dates', 'Date signed', { kind: 'submittedDate' });
  add('Signatures and dates', 'Date parent or guardian signed', { kind: 'parentSignedDate' });
  return choices;
}


/** A short name for one way a question fills a field, shown under the question's own heading. */
function questionEntry(field: Field, projection: Extract<PdfValueSource, { kind: 'question' }>['projection']): string {
  const named = (labels: string[] | undefined, ids: string[] | undefined, id: string, kind: string) => {
    const index = ids?.indexOf(id) ?? -1;
    return index < 0 ? `Removed ${kind}` : labels?.[index]?.trim() || `${kind[0].toUpperCase()}${kind.slice(1)} ${index + 1}`;
  };
  switch (projection.kind) {
    case 'text':
      if (projection.numberFormat === 'fraction') return 'Answer out of the maximum (such as 4/5)';
      if (['choice', 'dropdown'].includes(field.type)) return 'Selected option (its name)';
      if (field.grid) return 'All answers (one line per row)';
      return 'Answer';
    case 'optionSelected': return named(field.options, field.optionIds, projection.optionId, 'option');
    case 'gridRowText': return `${named(field.grid?.rows, field.grid?.rowIds, projection.rowId, 'row')} (selected options)`;
    case 'gridCellSelected': return `${named(field.grid?.rows, field.grid?.rowIds, projection.rowId, 'row')}: ${named(field.grid?.columns, field.grid?.columnIds, projection.columnId, 'column')}`;
    // What they typed goes in a text field, and whether they chose it in a checkbox, so a list only ever offers one of them.
    case 'otherText': case 'otherSelected': return 'Other';
  }
}
